import PageView from "components/common/page/PageView";
import { AppRoutes } from "config/router";
import { Roles } from "utilities/app-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import { AddPersonToTagDocument, AddPersonToTagMutation, AddPersonToTagMutationVariables, GetPersonTagDocument, GetPersonTagQuery, PersonFilterInput, PersonSortInput } from "types/graphql";
import { ItemViewPropsType } from "components/common/views/ItemView";
import NoData from "components/common/NoData";
import PersonDatalist, { PersonDatalistPropsType } from "components/entities/app/person/PersonDatalist";
import Translate from "components/common/Translate";
import { useParams } from "react-router-dom";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import ModalPortal from "components/modal/ModalPortal";
import Form from "components/common/form/Form";
import { useRef, useState } from "react";
import PersonDropdown, { PersonDropdownPropsType } from "components/entities/app/person/partials/inputs/PersonDropdown";
import FormActions from "components/common/form/partials/FormActions";
import { PersonType } from "types/person-types";
import { gql } from "@apollo/client";
import { graph } from "config/axios-context";
import Datalist from "components/common/datalist/Datalist";

const ADD_PERSON_TO_TAG_MODAL = "person-tag-view-page-add";

var addPersonToTagMutation = gql`
mutation AddPersonToTag($model: PersonTagLinkInput!) {
    personTagLink(model: $model){
      id
    }
  }
`;

// FIXME: fix this request dose not working
var deletePersonToTagMutation = gql`
mutation DeletePersonToTag($id: Int, $where: PersonTagLinkFilterInput) {
    personTagLinkDelete(id: $id, where: $where)
  }
`;

const PersonTagViewPage = () => {
    const context = useAppContext();
    const params = useParams<{ id: string }>();
    const [model, setModel] = useState<PersonType>();
    const datalistRef = useRef<Datalist<PersonType, PersonFilterInput, PersonSortInput>>(null);

    const personDatalistConfig: PersonDatalistPropsType = {
        name: "person-tag-view-people-datalist",
        onItemClick: {
            link: {
                url: AppRoutes.PERSON_VIEW,
                params: (item) => ({ id: item.id })
            }
        },
        actions: {
            delete: async i => {
                try {
                    const response = await graph({
                        query: deletePersonToTagMutation,
                        variables: {
                            where: {
                                and: [{
                                    personId: {
                                        eq: i
                                    }
                                }, {
                                    tagId: {
                                        eq: Number(params.id)
                                    }
                                }]
                            }
                        }
                    });

                    if (response.errors) {
                        throw response.errors;
                    }
                    context.notify('success', 'request.delete.success');
                } catch (error: any) {
                    context.notify('error', 'request.delete.error', Array.isArray(error) ? error[0].message : error.message);
                    throw error;
                }
            },
            mutationPermissionRole: Roles.PERSON_WRITE
        },
        fixedFilters: {
            tags: {
                some: {
                    tag: {
                        id: {
                            eq: Number(params.id)
                        }
                    }
                }
            }
        },
        header: {
            rightButtons: [{
                icon: faPlus,
                title: 'button.add',
                name: 'add',
                onClick: () => {
                    context.modal.open(ADD_PERSON_TO_TAG_MODAL);
                }
            }]
        }
    };

    const personDropdownConfig: Partial<PersonDropdownPropsType> = {
        where: {
            tags: {
                none: {
                    tagId: {
                        eq: Number(params.id)
                    }
                }
            }
        },
        name: "person-tag-view-page-add-person-dropdown",
        addPerson: false,
        onChange: (person) => {
            setModel(person);
        }
    }

    const view = (query: GetPersonTagQuery) => {
        if (!query.personTag) return <NoData />;
        var model = query.personTag!;
        var properties: ItemViewPropsType['model'] = [{
            name: 'all',
            items: [{
                name: 'id',
                label: 'fields.id.name',
                value: model.id
            }, {
                name: 'name',
                label: 'fields.name.name',
                value: model.name
            }]
        }]
        return properties;
    }

    const onSubmit = async () => {
        try {
            const response = await graph<AddPersonToTagMutation, AddPersonToTagMutationVariables>({
                query: AddPersonToTagDocument,
                variables: {
                    model: {
                        personId: model!.id,
                        tagId: Number(params.id),
                        id: 0
                    }
                }
            });

            if (response.errors) {
                throw response.errors;
            }
            context.modal.close(ADD_PERSON_TO_TAG_MODAL);
            datalistRef.current?.reload();
            setTimeout(() => {
                setModel(undefined) 
            }, 10);
        } catch (error: any) {
            context.notify('error', 'error.loading-data', Array.isArray(error) ? error[0].message : error.message);
            throw error;
        }
    }

    return (
        <>
            <PageView
                title="pages.people.tag.view"
                editUrl={context.userHasRole(Roles.PERSON_WRITE) ? AppRoutes.PERSON_TAG_EDIT : undefined}
                onDelete={context.userHasRole(Roles.PERSON_WRITE) ? "personTagDelete" : undefined}
                query={GetPersonTagDocument}
                queryDataPropertyName="personTag"
                wFull
                view={view}
                name="person-tag-view-page"
            >
                <div className="w-full">
                    <div className="pt-8">
                        <hr />
                        <h3 className="text-lg font-medium text-neutral-500">
                            <Translate i18nKey={"pages.people.list"} />:
                        </h3>
                        <PersonDatalist
                            {...personDatalistConfig}
                            datalistRef={datalistRef}
                        />
                    </div>
                </div>
            </PageView>
            <ModalPortal name={ADD_PERSON_TO_TAG_MODAL} title="common.add">
                <Form name={ADD_PERSON_TO_TAG_MODAL} onSubmit={onSubmit} >
                    <PersonDropdown
                        {...personDropdownConfig}
                        value={model}
                    />
                    <FormActions />
                </Form>
            </ModalPortal>
        </>
    );
};

export default PersonTagViewPage;
